import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "utils/i18n";

const SHORT_LABELS = {
  en: "EN",
  hi: "हिं",
  "hi-Latn": "Hi-En",
};

export default function LanguageSwitcher() {
  const { t, i18n } = useTranslation("home");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = SUPPORTED_LANGUAGES.includes(i18n.language)
    ? i18n.language
    : "en";

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (lng) => {
    i18n.changeLanguage(lng);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("language.label")}
        className="flex items-center gap-1 h-[38px] px-[12px] rounded-full bg-white/15 backdrop-blur-md border border-white/30 hover:bg-white/25 transition-colors text-xs md:text-sm font-semibold text-white whitespace-nowrap"
      >
        <span aria-hidden="true">🌐</span>
        <span>{SHORT_LABELS[current]}</span>
        <span aria-hidden="true" className="text-white/70">
          ▾
        </span>
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t("language.label")}
          className="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50"
        >
          {SUPPORTED_LANGUAGES.map((lng) => (
            <li key={lng} role="option" aria-selected={lng === current}>
              <button
                type="button"
                onClick={() => choose(lng)}
                lang={lng}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-sm text-left transition-colors ${
                  lng === current
                    ? "bg-pink-50 text-pink-600 font-bold"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <span>{t(`language.${lng}`)}</span>
                {lng === current && <span aria-hidden="true">✓</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Inline version for the mobile hamburger menu: the three languages as
// side-by-side buttons, so changing language is a single tap.
// `glass`: light-on-dark look for the mobile menu's dark glass panel.
export function LanguageOptions({ onChange, glass = false }) {
  const { t, i18n } = useTranslation("home");
  const current = SUPPORTED_LANGUAGES.includes(i18n.language)
    ? i18n.language
    : "en";

  return (
    <div className="px-4 py-3">
      <div
        className={`text-xs font-semibold uppercase tracking-wider mb-2 ${glass ? "text-white/60" : "text-gray-400"}`}
      >
        🌐 {t("language.label")}
      </div>
      <div
        role="radiogroup"
        aria-label={t("language.label")}
        className={`grid grid-cols-3 gap-1 p-1 rounded-xl ${glass ? "bg-white/10" : "bg-gray-100"}`}
      >
        {SUPPORTED_LANGUAGES.map((lng) => (
          <button
            key={lng}
            type="button"
            role="radio"
            aria-checked={lng === current}
            lang={lng}
            onClick={() => {
              i18n.changeLanguage(lng);
              if (onChange) onChange(lng);
            }}
            className={`py-2 rounded-lg text-sm font-semibold transition-colors border-0 ${
              lng === current
                ? "bg-white text-pink-600 shadow"
                : glass
                  ? "bg-transparent text-white/75 hover:text-white"
                  : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t(`language.${lng}`)}
          </button>
        ))}
      </div>
    </div>
  );
}
